import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Globe, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Building, 
  Briefcase,
  Languages as LanguagesIcon,
  GraduationCap,
  Linkedin,
  Twitter,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Download,
  X,
  Edit,
  Info,
  Image,
  BookOpen,
  ExternalLink,
  Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { getCookie } from '@/utils/cookies';
import apolloClient from '@/lib/apollo-client';
import { GET_PROVIDER_BY_ID } from '@/lib/graphql/queries/providerQueries';
import { Provider } from '@/features/provider/types';
import ProviderLogo from '@/components/common/ProviderLogo';
import ProviderImage from '@/components/common/ProviderImage';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';

// Format provider type for display
const formatProviderType = (type: string) => {
  if (!type) return '';
  return type.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const ProviderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  
  // Function to navigate to previous image
  const prevImage = () => {
    if (!provider?.imageUrls?.length) return;
    setActiveImage((prev) => (prev === 0 ? provider.imageUrls.length - 1 : prev - 1));
  };

  // Function to navigate to next image
  const nextImage = () => {
    if (!provider?.imageUrls?.length) return;
    setActiveImage((prev) => (prev === provider.imageUrls.length - 1 ? 0 : prev + 1));
  };

  // Function to open lightbox with specific image
  const openLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setShowLightbox(true);
  };
  
  // Fetch provider data
  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const token = getCookie('token');
        const providerId = parseInt(id, 10);
        
        if (isNaN(providerId)) {
          throw new Error('Invalid provider ID');
        }
        
        const { data } = await apolloClient.query({
          query: GET_PROVIDER_BY_ID,
          variables: { id: providerId },
          context: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          },
          fetchPolicy: 'network-only' // Don't use cache for this query
        });
        
        if (!data || !data.provider) {
          throw new Error('Provider not found');
        }
        
        // Handle potential field name differences between API and interface
        const providerData = {
          ...data.provider,
          // Ensure degreeTypes is always an array
          degreeTypes: data.provider.degreeTypes || 
                      (data.provider.degreeType ? [data.provider.degreeType] : [])
        };
        
        setProvider(providerData);
      } catch (err) {
        console.error('Error fetching provider:', err);
        setError(err instanceof Error ? err.message : 'Failed to load provider details');
        toast({
          title: "Error",
          description: "Failed to load provider details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [id, toast]);

  // Add keyboard navigation useEffect - moved up before conditional returns
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showLightbox) return;
      
      if (e.key === 'ArrowLeft') {
        prevImage();
        if (provider?.imageUrls) {
          setLightboxImage(provider.imageUrls[activeImage]);
        }
      } else if (e.key === 'ArrowRight') {
        nextImage();
        if (provider?.imageUrls) {
          setLightboxImage(provider.imageUrls[activeImage]);
        }
      } else if (e.key === 'Escape') {
        setShowLightbox(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, activeImage, provider?.imageUrls]);

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[calc(100vh-5rem)] ml-0 sm:ml-0 lg:ml-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="container mx-auto py-8 pt-20 ml-0 sm:ml-0 lg:ml-64">
        <div className="flex items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Providers
          </Button>
        </div>
        <Card className="bg-destructive/10 border-destructive/20">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error || 'Provider not found'}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="mt-4"
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Sticky header with navigation and actions */}
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-4 border-b shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(-1)} 
            className="group flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="font-medium">Back to Providers</span>
          </Button>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast({
                  title: "Link Copied",
                  description: "Share this provider with others",
                });
              }}
              className="transition-all hover:bg-primary/10"
            >
              <Share2 className="mr-2 h-4 w-4" /> Share
            </Button>
            <Button 
              onClick={() => navigate(`/admin/providers/edit/${id}`)}
              size="sm"
              className="bg-primary hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </div>
        </div>
      </div>

      {/* Hero Banner with Image Background - similar to TrainingDetails */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          {provider.imageUrls && provider.imageUrls.length > 0 ? (
            <ProviderImage
              providerId={parseInt(id!, 10)}
              filename={provider.imageUrls[0]}
              alt={provider.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/30 to-primary/10"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/80"></div>
        </div>
        <div className="container relative z-10 mx-auto px-4 py-16 md:py-24 max-w-7xl">
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="secondary" className="bg-white/20 text-white border-none hover:bg-white/30">
              {formatProviderType(provider.type)}
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white border-none hover:bg-white/30 capitalize">
              {provider.ownership}
            </Badge>
            {provider.category && (
              <Badge variant="secondary" className="bg-white/20 text-white border-none hover:bg-white/30">
                {provider.category}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-6 mb-6">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden border-4 border-white/30 bg-white/10 shadow-lg">
              {provider.logoUrl ? (
                <ProviderLogo 
                  filename={provider.logoUrl}
                  alt={`${provider.name} logo`}
                  className="w-full h-full object-contain p-2"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-white/10">
                  <span className="text-white font-bold text-4xl">
                    {provider.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight max-w-4xl">{provider.name}</h1>
              {provider.yearFounded && (
                <div className="flex items-center mt-2 text-white/80">
                  <Calendar className="h-5 w-5 mr-2" />
                  <span>Founded in {provider.yearFounded}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
            {provider.address && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
                <div className="bg-white/20 rounded-full p-3 mr-4">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Location</p>
                  <p className="font-semibold">
                    {provider.city || provider.state || provider.country ? 
                      `${provider.city || ''} ${provider.state || ''} ${provider.country || ''}`.trim() : 
                      'Address available'}
                  </p>
                </div>
              </div>
            )}
            
            {provider.email && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
                <div className="bg-white/20 rounded-full p-3 mr-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Contact</p>
                  <p className="font-semibold truncate max-w-[180px]">{provider.email}</p>
                </div>
              </div>
            )}
            
            {provider.languages && provider.languages.length > 0 && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
                <div className="bg-white/20 rounded-full p-3 mr-4">
                  <LanguagesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-sm">Languages</p>
                  <p className="font-semibold">{provider.languages.slice(0, 2).join(', ')}{provider.languages.length > 2 ? '...' : ''}</p>
                </div>
              </div>
            )}
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center">
              <div className="bg-white/20 rounded-full p-3 mr-4">
                <Building className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Type</p>
                <p className="font-semibold">{formatProviderType(provider.type)}</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3 mt-8">
            {provider.website && (
              <a 
                href={provider.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 transition-colors rounded-full px-5 py-2 flex items-center gap-2 text-sm font-medium"
              >
                <Globe className="h-4 w-4" />
                Visit Website
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
            
            {provider.linkedin && (
              <a 
                href={provider.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 transition-colors rounded-full px-5 py-2 flex items-center gap-2 text-sm font-medium"
              >
                <Linkedin className="h-4 w-4" />
                LinkedIn
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
            
            {provider.x && (
              <a 
                href={provider.x} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white/20 hover:bg-white/30 transition-colors rounded-full px-5 py-2 flex items-center gap-2 text-sm font-medium"
              >
                <Twitter className="h-4 w-4" />
                Twitter
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            {/* About section */}
            <Card className="shadow-md overflow-hidden border-none">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent pb-3">
                <CardTitle className="text-2xl flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full mr-3">
                    <Info className="h-5 w-5 text-primary" />
                  </div>
                  About This Provider
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-muted-foreground leading-relaxed text-base whitespace-pre-wrap">
                  {provider.description}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div className="bg-muted/10 p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2 text-gray-800 flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Type
                    </h3>
                    <p className="text-gray-700">{formatProviderType(provider.type)}</p>
                  </div>
                  
                  <div className="bg-muted/10 p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2 text-gray-800 flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-primary" />
                      Ownership
                    </h3>
                    <p className="text-gray-700 capitalize">{provider.ownership}</p>
                  </div>
                  
                  {provider.yearFounded && (
                    <div className="bg-muted/10 p-4 rounded-lg">
                      <h3 className="text-lg font-medium mb-2 text-gray-800 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        Founded
                      </h3>
                      <p className="text-gray-700">{provider.yearFounded}</p>
                    </div>
                  )}
                  
                  {provider.languages && provider.languages.length > 0 && (
                    <div className="bg-muted/10 p-4 rounded-lg">
                      <h3 className="text-lg font-medium mb-2 text-gray-800 flex items-center gap-2">
                        <LanguagesIcon className="h-5 w-5 text-primary" />
                        Languages
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {provider.languages.map(language => (
                          <Badge key={language} variant="outline" className="px-3 py-1">
                            {language}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Programs & Degrees section */}
            <Card className="shadow-md overflow-hidden border-none">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent pb-3">
                <CardTitle className="text-2xl flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full mr-3">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  Programs & Degrees
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="bg-muted/10 p-5 rounded-lg">
                  <h3 className="text-lg font-medium mb-3 text-gray-800 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    Available Degree Types
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.degreeTypes && provider.degreeTypes.length > 0 ? (
                      provider.degreeTypes.map(degree => (
                        <Badge key={degree} variant="secondary" className="px-3 py-1">
                          {degree}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">No degree types specified</span>
                    )}
                  </div>
                </div>
                
                <div className="bg-muted/10 p-5 rounded-lg">
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Category</h3>
                  <p className="text-gray-700">{provider.category || 'Not specified'}</p>
                </div>
                
                <div className="bg-muted/10 p-5 rounded-lg">
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Languages of Instruction</h3>
                  <p className="text-gray-700">{provider.languages?.join(', ') || 'Not specified'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right column - Gallery */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="shadow-lg border-none overflow-hidden sticky top-24">
              <CardHeader className="bg-gradient-to-r from-primary/20 to-primary/5 pb-3">
                <CardTitle className="text-xl flex items-center">
                  <div className="bg-primary/10 p-2 rounded-full mr-3">
                    <Image className="h-5 w-5 text-primary" />
                  </div>
                  Gallery
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {provider?.imageUrls && provider.imageUrls.length > 0 ? (
                  <div className="space-y-4">
                    {/* Main Image Display */}
                    <div 
                      className="relative rounded-lg overflow-hidden h-48 md:h-64 bg-gray-100 shadow-inner group cursor-pointer"
                      onClick={() => openLightbox(provider.imageUrls[activeImage])}
                    >
                      <ProviderImage
                        providerId={parseInt(id!, 10)}
                        filename={provider.imageUrls[activeImage]}
                        alt={`${provider.name} - Image ${activeImage + 1}`}
                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white/90 rounded-full p-3 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                          <ZoomIn className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Thumbnails */}
                    {provider.imageUrls.length > 1 && (
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm text-muted-foreground">
                            {activeImage + 1} / {provider.imageUrls.length}
                          </span>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={prevImage}
                              className="rounded-full h-8 w-8 p-0 hover:bg-primary/10 transition-colors"
                              aria-label="Previous image"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={nextImage}
                              className="rounded-full h-8 w-8 p-0 hover:bg-primary/10 transition-colors"
                              aria-label="Next image"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {provider.imageUrls.map((url, index) => (
                            <div 
                              key={`thumb-${index}`}
                              className={`relative cursor-pointer rounded-md overflow-hidden h-16 flex-shrink-0 border-2 transition-all ${
                                activeImage === index ? 'border-primary shadow-md' : 'border-transparent hover:border-gray-300'
                              }`}
                              onClick={() => setActiveImage(index)}
                            >
                              <ProviderImage
                                providerId={parseInt(id!, 10)}
                                filename={url}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {activeImage === index && (
                                <div className="absolute inset-0 bg-primary/10 pointer-events-none" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="bg-gray-100 p-6 rounded-full mb-4">
                      <ImageIcon className="h-12 w-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-700">No Images Available</h3>
                    <p className="text-sm text-gray-500 mt-2">This provider doesn't have any images in the gallery.</p>
                  </div>
                )}
              </CardContent>
              
              {provider.address && (
                <div className="border-t p-6">
                  <h3 className="font-medium mb-3 flex items-center text-lg">
                    <MapPin className="h-5 w-5 mr-2 text-primary" />
                    Full Address
                  </h3>
                  <div className="bg-muted/10 p-4 rounded-lg">
                    <p className="text-gray-700">
                      {provider.address}
                      {provider.city && `, ${provider.city}`}
                      {provider.state && `, ${provider.state}`}
                      {provider.country && `, ${provider.country}`}
                      {provider.postalCode && ` ${provider.postalCode}`}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Lightbox for full-size image viewing */}
      <Dialog open={showLightbox} onOpenChange={setShowLightbox}>
        <DialogContent className="max-w-screen-lg w-[95vw] sm:w-[90vw] h-[90vh] p-0 bg-black/95 border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {lightboxImage && (
              <ProviderImage
                providerId={parseInt(id!, 10)}
                filename={lightboxImage}
                alt={`${provider?.name} - Full size image`}
                className="max-w-full max-h-full object-contain"
              />
            )}
            
            <div className="absolute top-4 right-4 flex gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors h-10 w-10"
                onClick={() => {
                  // Handle download functionality
                  const link = document.createElement('a');
                  link.href = `/api/providers/${id}/images/${lightboxImage}`;
                  link.download = lightboxImage || 'provider-image';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                aria-label="Download image"
              >
                <Download className="h-5 w-5" />
              </Button>
              <DialogClose asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors h-10 w-10"
                  aria-label="Close lightbox"
                >
                  <X className="h-5 w-5" />
                </Button>
              </DialogClose>
            </div>
            
            {provider?.imageUrls && provider.imageUrls.length > 1 && (
              <>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors h-10 w-10"
                  onClick={() => {
                    prevImage();
                    setLightboxImage(provider.imageUrls[activeImage]);
                  }}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors h-10 w-10"
                  onClick={() => {
                    nextImage();
                    setLightboxImage(provider.imageUrls[activeImage]);
                  }}
                  aria-label="Next image"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
            
            {/* Image counter and keyboard navigation hints in lightbox */}
            {provider?.imageUrls && provider.imageUrls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-3">
                <span>{activeImage + 1} / {provider.imageUrls.length}</span>
                <div className="hidden md:flex items-center gap-1 text-xs text-gray-300">
                  <span className="px-1.5 py-0.5 bg-black/30 rounded border border-gray-600">←</span>
                  <span className="px-1.5 py-0.5 bg-black/30 rounded border border-gray-600">→</span>
                  <span className="ml-1">to navigate</span>
                  <span className="px-1.5 py-0.5 bg-black/30 rounded border border-gray-600 ml-2">ESC</span>
                  <span className="ml-1">to close</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderDetail;

